import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './AuthContext'
import api from '../services/api.js'
import { sendRequest } from '../../core/api/send.js'

const PlatformContext = createContext(null)

export function PlatformProvider({ children }) {
  const { isAuthenticated, getAccessToken, isLocalUser } = useAuth()
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [tenants, setTenants] = useState([])
  const [selectedTenantId, setSelectedTenantId] = useState(() => {
    const stored = localStorage.getItem('platformTenantId')
    return stored ? parseInt(stored, 10) : null
  })
  const [selectedTenantName, setSelectedTenantName] = useState(() => {
    return localStorage.getItem('platformTenantName') || null
  })
  const [impersonating, setImpersonating] = useState(() => {
    const stored = localStorage.getItem('platformImpersonating')
    return stored ? JSON.parse(stored) : null
  })
  const [loading, setLoading] = useState(true)
  const registeredRef = useRef(false)

  // Register platform headers getter with the API client
  const getPlatformHeaders = useCallback(() => {
    const headers = {}
    if (selectedTenantId) {
      headers['X-Tenant-Id'] = String(selectedTenantId)
    }
    if (impersonating?.email) {
      headers['X-Impersonate-Email'] = impersonating.email
    }
    return headers
  }, [selectedTenantId, impersonating])

  useEffect(() => {
    api.setPlatformHeadersGetter(getPlatformHeaders)
    registeredRef.current = true
    return () => {
      if (registeredRef.current) {
        api.setPlatformHeadersGetter(null)
      }
    }
  }, [getPlatformHeaders])

  // Check platform admin status on auth
  useEffect(() => {
    if (!isAuthenticated) {
      setIsPlatformAdmin(false)
      setLoading(false)
      return
    }

    const checkStatus = async () => {
      try {
        const token = getAccessToken()
        const res = await fetch('/api/platform/status', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (res.ok) {
          const data = await res.json()
          setIsPlatformAdmin(data.isPlatformAdmin === true)
        } else {
          setIsPlatformAdmin(false)
        }
      } catch {
        setIsPlatformAdmin(false)
      } finally {
        setLoading(false)
      }
    }
    checkStatus()
  }, [isAuthenticated, getAccessToken])

  // Load tenants when platform admin is confirmed
  useEffect(() => {
    if (!isPlatformAdmin) {
      setTenants([])
      return
    }

    const loadTenants = async () => {
      try {
        const token = getAccessToken()
        const res = await fetch('/api/platform/tenants', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (res.ok) {
          const data = await res.json()
          setTenants(data.tenants || [])
        }
      } catch {
        // silent
      }
    }
    loadTenants()
  }, [isPlatformAdmin, getAccessToken])

  const switchTenant = useCallback((tenantId, tenantName) => {
    setSelectedTenantId(tenantId)
    setSelectedTenantName(tenantName)
    if (tenantId) {
      localStorage.setItem('platformTenantId', String(tenantId))
      localStorage.setItem('platformTenantName', tenantName || '')
    } else {
      localStorage.removeItem('platformTenantId')
      localStorage.removeItem('platformTenantName')
    }
    // Clear impersonation when switching tenants
    setImpersonating(null)
    localStorage.removeItem('platformImpersonating')
  }, [])

  const startImpersonation = useCallback(async (email, name, tenantId) => {
    const imp = { email, name }
    setImpersonating(imp)
    localStorage.setItem('platformImpersonating', JSON.stringify(imp))

    // If a tenantId is provided and different from current, switch
    if (tenantId && tenantId !== selectedTenantId) {
      // Find tenant name
      const t = tenants.find(t => t.id === tenantId)
      switchTenant(tenantId, t?.name || `Tenant ${tenantId}`)
    }

    // Log to server
    try {
      const token = getAccessToken()
      await sendRequest('/api/platform/impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ email, tenantId: tenantId || selectedTenantId })
      })
    } catch { /* deliberate: the tenant simply does not switch, which is visible */ }
  }, [selectedTenantId, tenants, switchTenant, getAccessToken])

  const stopImpersonation = useCallback(async () => {
    setImpersonating(null)
    localStorage.removeItem('platformImpersonating')

    try {
      const token = getAccessToken()
      await sendRequest('/api/platform/stop-impersonate', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
    } catch { /* deliberate: the tenant simply does not switch, which is visible */ }
  }, [getAccessToken])

  const exitPlatformView = useCallback(() => {
    setSelectedTenantId(null)
    setSelectedTenantName(null)
    setImpersonating(null)
    localStorage.removeItem('platformTenantId')
    localStorage.removeItem('platformTenantName')
    localStorage.removeItem('platformImpersonating')
  }, [])

  const refreshTenants = useCallback(async () => {
    if (!isPlatformAdmin) return
    try {
      const token = getAccessToken()
      const res = await fetch('/api/platform/tenants', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      if (res.ok) {
        const data = await res.json()
        setTenants(data.tenants || [])
      }
    } catch { /* deliberate: the tenant simply does not switch, which is visible */ }
  }, [isPlatformAdmin, getAccessToken])

  const value = {
    isPlatformAdmin,
    loading,
    tenants,
    selectedTenantId,
    selectedTenantName,
    impersonating,
    switchTenant,
    startImpersonation,
    stopImpersonation,
    exitPlatformView,
    refreshTenants,
    getPlatformHeaders
  }

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  )
}

export function usePlatform() {
  const context = useContext(PlatformContext)
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider')
  }
  return context
}

export default PlatformContext
